// Angular Files
import {
    Component,
    EventEmitter,
    HostBinding,
    Input,
    Output
} from '@angular/core';

// Component Files
import {
    ScheduleDetailsViewModel,
    SchedulePaymentRow
} from './schedule-details.models';

@Component({
    selector: 'teller-online-schedule-details',
    templateUrl: './schedule-details.component.html',
    styleUrls: ['./schedule-details.component.scss'],
    standalone: false
})
export class TellerOnlineScheduleDetailsComponent {
    @HostBinding('class') hostClass = 'schedule-details-view';

    // Public variables
    @Input() public schedule: ScheduleDetailsViewModel;

    @Input() public currentPaymentId: number;

    @Input() public ariaLabelledBy: string;

    @Output() public rowClick = new EventEmitter<SchedulePaymentRow>();

    public get displayedColumns(): string[] {
        if (this.schedule?.showFees) {
            return ['date', 'amount', 'fee', 'total'];
        }

        return ['date', 'amount'];
    }

    public get hasAdjustedFinalPayment(): boolean {
        return this.schedule?.payments
            ?.some(p => p.amountMarker === '**')
            ?? false;
    }

    // Consumers may hand over freshly built rows on every change detection (the public modal's
    // view model is a getter). Tracking by id keeps each row's DOM, so a focused row button
    // survives the pass instead of being destroyed and dropping focus to the body.
    public trackByPaymentId(
        _index: number,
        payment: SchedulePaymentRow
    ): number {
        return payment.id;
    }

    public isPendingPayment(
        payment: SchedulePaymentRow
    ): boolean {
        return payment.status === 'Pending';
    }

    public isSelectedPayment(payment: SchedulePaymentRow): boolean {
        return payment.id === this.currentPaymentId;
    }

    public isClickable(payment: SchedulePaymentRow): boolean {
        return (payment?.clickable === true)
            && !this.isSelectedPayment(payment);
    }

    // Bound to (pointerdown), not (click), because the latter
    // is intercepted by MatDialog's CDK FocusTrap when the
    // row is hosted inside a dialog — the first click is
    // consumed by focus management and the user has to click
    // twice for navigation to happen. Pointer events fire
    // earlier in the event cycle and bypass the click-dispatch
    // path entirely.
    public onPointerdown_row(
        payment: SchedulePaymentRow,
        event: PointerEvent
    ): void {
        // Only respond to primary mouse button; allow all
        // touch/pen contacts (their button is 0 already).
        if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
        }

        if (!this.isClickable(payment)) {
            return;
        }

        this.rowClick.emit(payment);
    }

    // Keyboard and assistive-tech activation of a row's date button: Enter / Space and a screen
    // reader's activate raise a click with detail 0. A pointer click (detail 1+) was already
    // handled by onPointerdown_row, so it is ignored here rather than navigating twice.
    public onClick_rowAction(
        payment: SchedulePaymentRow,
        event: MouseEvent
    ): void {
        if (event.detail !== 0 || !this.isClickable(payment)) {
            return;
        }

        this.rowClick.emit(payment);
    }
}
